/* eslint-disable max-len */
import { CustomAttributeOptionPropertiesData } from './customAttributesOptionProperties';
import { CustomAttributeIntegerPropertiesData } from './customAttributesIntegerProperties';
import { CustomAttributeTextPropertiesData } from './customAttributesTextProperties';
import { CustomAttributeDecimalPropertiesData } from './customAttributesDecimalProperties';
import { CustomAttributeType } from './customAttributeType';
import { CustomAttributeTranslationProperties } from './customAttributesTranslationProperties';
import { CustomAttributeValue } from './customAttributeValue';
import { CustomAttributeClassPrefix } from './customAttributesClassPrefix';
// eslint-disable-next-line import/no-cycle
import { PriceData, PriceDataHelpers } from '../fileFormats/catalog/catalog';
// eslint-disable-next-line import/no-cycle
import { ConditionalRangeRuleSet } from './customAttributesConditionalRanges';

export class CustomAttributeData {

  // Identification
  public _id: string | undefined;

  // Properties
  public name : string | undefined;
  public translations : Array<CustomAttributeTranslationProperties> | undefined;
  public key : string | undefined;
  public type : CustomAttributeType | undefined;
  public isMandatory : boolean = false;
  public priority : number = 0;
  public hasQuantity: boolean = false;
  public basePrice: number = 0;

  // presentation properties
  public color: string = '#000000';
  public hyperLink: string = '';

  // Condition string
  public condition: string = '';

  // Properties for text values
  public propertiesText : CustomAttributeTextPropertiesData | undefined;

  // Properties for numeric values
  public propertiesInteger : CustomAttributeIntegerPropertiesData | undefined;

  // Properties for numeric values
  public propertiesDecimal : CustomAttributeDecimalPropertiesData | undefined;

  // Properties for option
  public propertiesOption : CustomAttributeOptionPropertiesData | undefined;

  // Conditional Rule set (i.e. ranges that depende on other attributes values)
  public conditionalRangeRuleSet : ConditionalRangeRuleSet | undefined;

  public selectedLang: string | undefined;

  public static isKeyFromClass(
    attrKey : string | undefined,
    classPrefix : CustomAttributeClassPrefix,
  ) : boolean {
    if (attrKey) return attrKey.startsWith(classPrefix);
    return false;
  }

  public static isFromClass(
    attr : CustomAttributeData,
    classPrefix : CustomAttributeClassPrefix,
  ) : boolean {
    if (attr.key) return attr.key.startsWith(classPrefix);
    return false;
  }

  public static isExtenderOf(
    attr : CustomAttributeData,
    optionGroup: string,
  ) : boolean {
    if (CustomAttributeData.isFromClass(attr, CustomAttributeClassPrefix.Extra)
      || CustomAttributeData.isFromClass(attr, CustomAttributeClassPrefix.Extension)) {
      const stripedGroupNameWithExtraPrefix : string = `${CustomAttributeClassPrefix.Extra}${optionGroup.replaceAll(' ', '')}`;
      const stripedGroupNameWithExtendPrefix : string = `${CustomAttributeClassPrefix.Extra}${optionGroup.replaceAll(' ', '')}`;
      return attr.key === stripedGroupNameWithExtraPrefix || attr.key === stripedGroupNameWithExtendPrefix;
    }
    return false;
  }

  public static getAttributePrice(attrV: CustomAttributeValue, priceData: PriceData[] | undefined) : number {
    return this.getAttributeValuePrice(attrV.attributeKey, attrV.attributeValue, attrV.attributeBasePrice, priceData);
  }

  public static getAttributeValuePrice(
    key: string | undefined,
    value: string | undefined,
    basePrice: number,
    priceData: PriceData[] | undefined,
  ) : number {
    if (priceData && key && value) {
      return PriceDataHelpers.getOptionPrice(
        priceData,
        'customAttributes',
        key,
        value,
      ) ?? basePrice;
    }
    return basePrice;
  }

  // Helper functions
  public static calculateAttributesTotalPrice(
    attributeSettings : CustomAttributeData[],
    attributeValues: CustomAttributeValue[],
    prefixException: string | undefined,
    prefixMandatory: string | undefined,
    priceData: PriceData[] | undefined,
  ) : number {
    let totalCustomAttrPrice = 0.0;
    if (attributeSettings !== undefined) {
      attributeValues.forEach((attVal) => {
        // Let's see if this attribute is to be considered in the price
        const skipAttribute = (prefixException ? attVal.attributeKey.startsWith(prefixException) : false)
        || (prefixMandatory ? !attVal.attributeKey.startsWith(prefixMandatory) : false);

        // eslint-disable-next-line max-len
        if (!skipAttribute) {
          const customAttrDefinition = attributeSettings.find((at) => { return at.key === attVal.attributeKey; });
          if (customAttrDefinition !== undefined && customAttrDefinition !== null) {
            if (customAttrDefinition.basePrice > 0) {
              const effectiveQuantity = customAttrDefinition.hasQuantity ? attVal.attributeQuantity : 1;
              switch (customAttrDefinition.type) {
                case CustomAttributeType.Boolean:
                  totalCustomAttrPrice += attVal.attributeValue === 'true' ? this.getAttributePrice(attVal, priceData) * effectiveQuantity : 0;
                  break;
                case CustomAttributeType.Option:
                  totalCustomAttrPrice += attVal.attributeValue !== ' ' && attVal.attributeValue !== '' ? this.getAttributePrice(attVal, priceData) * effectiveQuantity : 0;
                  break;
                case CustomAttributeType.Text:
                  totalCustomAttrPrice += attVal.attributeValue !== 'false' ? this.getAttributePrice(attVal, priceData) * effectiveQuantity : 0;
                  break;
                default:
              }
            }
          }
        }
      });
    }
    return totalCustomAttrPrice;
  }

  public static getAttributeQuantity(
    attrDef: CustomAttributeData | undefined,
    // eslint-disable-next-line max-len
    attributeValue: CustomAttributeValue | undefined,
    requestedValue: string,
  ) : number {

    if (attrDef === undefined) {
      return -1;
    }

    let realValue: string | undefined = requestedValue;
    switch (attrDef.type) {
      case CustomAttributeType.Option:

        if (attrDef.propertiesOption !== undefined && attrDef.propertiesOption.options !== undefined) {
          realValue = attrDef.propertiesOption.options.find((v) => { return !v.localeCompare(requestedValue); });
          if (realValue === undefined) {
            // eslint-disable-next-line prefer-destructuring
            realValue = attrDef.propertiesOption.options[0];
          }
        }
        if (realValue === '' || realValue === ' ') {
          return 0;
        }
        if (attributeValue !== undefined) {
          return attributeValue.attributeQuantity > 0 ? attributeValue.attributeQuantity : 1;
        }
        return 1;
        break;

      case CustomAttributeType.Boolean:

        if (realValue === undefined) {
          // eslint-disable-next-line prefer-destructuring
          realValue = 'false';
        }
        if (realValue !== 'true') {
          return 0;
        }
        if (attributeValue !== undefined) {
          return attributeValue.attributeQuantity > 0 ? attributeValue.attributeQuantity : 1;
        }
        return 1;
        break;
      case CustomAttributeType.Text:

        if (realValue === undefined) {
          // eslint-disable-next-line prefer-destructuring
          realValue = 'false';
        }
        if (realValue === 'false' || realValue === '') {
          return 0;
        }
        if (attributeValue !== undefined) {
          return attributeValue.attributeQuantity > 0 ? attributeValue.attributeQuantity : 1;
        }
        return 1;
        break;
      default:
        return -1;
    }
  }

  public static getTranslatedOption(option: string, attr: CustomAttributeData) : string {
    if (attr.propertiesOption && attr.propertiesOption.optionTranslations && attr.propertiesOption.optionTranslations.length > 0) {
      return CustomAttributeOptionPropertiesData.translateOption(
        attr.propertiesOption,
        option,
        attr.selectedLang || 'pt',
      );
    }
    return option;
  }

}
